import React, { useState, useEffect } from "react";
import { Drawer, DrawerContent } from "./ui/drawer";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Badge } from "./ui/badge";
import { Minus, Plus } from "lucide-react";
import { gbp } from "../context/CartContext";

export default function ItemDrawer({ open, onOpenChange, item, editLine, onAdd, onUpdate }) {
  const [qty, setQty] = useState(1);
  const [selected, setSelected] = useState([]);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (open) {
      if (editLine) {
        setQty(editLine.qty);
        setSelected(editLine.extras.map((e) => e.id));
        setNotes(editLine.notes || "");
      } else {
        setQty(1); setSelected([]); setNotes("");
      }
    }
  }, [open, editLine]);

  if (!item) return null;
  const extras = item.extras || [];
  const chosenExtras = extras.filter((e) => selected.includes(e.id))
    .map((e) => ({ id: e.id, name: e.name, price: Number(e.price) }));
  const unit = Number(item.base_price ?? item.price) + chosenExtras.reduce((s, e) => s + e.price, 0);
  const total = unit * qty;

  const toggle = (id) => setSelected((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const submit = () => {
    if (editLine) onUpdate(editLine.lineId, { ...editLine, base_price: item.base_price }, qty, chosenExtras, notes);
    else onAdd(item, qty, chosenExtras, notes);
    onOpenChange(false);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[92vh]" data-testid="item-drawer">
        <div className="mx-auto w-full max-w-lg overflow-y-auto">
          {item.image_url && (
            <div className="h-44 w-full overflow-hidden">
              <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
            </div>
          )}
          <div className="p-5">
            <h2 className="font-head text-2xl font-bold text-brand-dark">{item.name}</h2>
            {item.description && <p className="text-brand-dark/60 text-sm mt-1">{item.description}</p>}
            {item.allergens?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {item.allergens.map((a) => (
                  <Badge key={a} variant="outline" className="text-[11px] border-amber-300 text-amber-700 bg-amber-50">{a}</Badge>
                ))}
              </div>
            )}

            {extras.length > 0 && (
              <div className="mt-5">
                <p className="font-semibold text-sm text-brand-dark mb-2">Customise</p>
                <div className="space-y-1.5">
                  {extras.map((e) => (
                    <label key={e.id} data-testid={`extra-${e.id}`}
                      className="flex items-center gap-3 p-3 rounded-xl border border-brand-dark/10 cursor-pointer active:scale-[0.99] transition-transform">
                      <Checkbox checked={selected.includes(e.id)} onCheckedChange={() => toggle(e.id)} />
                      <span className="flex-1 text-sm text-brand-dark">{e.name}</span>
                      {Number(e.price) > 0 && <span className="text-sm font-semibold text-brand-teal">+{gbp(e.price)}</span>}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-5">
              <p className="font-semibold text-sm text-brand-dark mb-2">Special instructions</p>
              <Textarea data-testid="item-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="No onions please" className="rounded-xl resize-none" rows={2} />
            </div>

            <div className="flex items-center justify-between mt-5">
              <div className="flex items-center gap-4 bg-brand-dark/5 rounded-full p-1.5">
                <button data-testid="qty-dec" onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-sm active:scale-90 transition-transform">
                  <Minus className="w-4 h-4" />
                </button>
                <span className="font-bold text-lg w-5 text-center" data-testid="qty-value">{qty}</span>
                <button data-testid="qty-inc" onClick={() => setQty((q) => q + 1)}
                  className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-sm active:scale-90 transition-transform">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            <Button data-testid="add-to-order-btn" onClick={submit}
              className="w-full mt-5 h-14 rounded-2xl bg-brand-teal hover:bg-brand-teal-dark text-white text-base font-bold active:scale-[0.98] transition-transform">
              {editLine ? "Update order" : "Add to order"} · {gbp(total)}
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
